import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import App from "./App";
import analytics from "./analytics";
import { installApiAuthInterceptor } from "./fetchInterceptor";
import { getGroupAccess, groupAccessEnabled } from "./groupAccess";
import "./AuthenticatedApp.css";

const API_BASE = process.env.REACT_APP_API_BASE || "";
const LOCAL_SIGNOUT_REDIRECT_URI = (
  process.env.REACT_APP_LOCAL_SIGNOUT_REDIRECT_URI || "https://www.kochinc.com/"
).trim();
const ALLOWED_EMAILS = (process.env.REACT_APP_ALLOWED_EMAILS || "")
  .split(",")
  .map((value) => value.trim().toLowerCase())
  .filter(Boolean);

function getUserEmail(profile) {
  for (const key of ["email", "preferred_username", "upn", "unique_name", "sub"]) {
    const value = profile?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim().toLowerCase();
    }
  }
  return "";
}

function AuthMessage({ title, children, action }) {
  return (
    <main className="auth-screen">
      <section className="auth-card">
        <div className="auth-logo-wrap">
          <img
            src={`${process.env.PUBLIC_URL || ""}/Logos/Koch-Technology-Lockup-horz-digital-black.ai.png`}
            alt="Koch Technology"
            className="auth-logo"
          />
        </div>
        <h1>{title}</h1>
        <p>{children}</p>
        {action}
      </section>
    </main>
  );
}

export default function AuthenticatedApp() {
  const auth = useAuth();
  const [tokenReady, setTokenReady] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [recoveredUser, setRecoveredUser] = useState(null);
  const [checkingRecovery, setCheckingRecovery] = useState(false);

  const currentUser = auth.user || recoveredUser;
  const token = currentUser?.id_token || "";
  const userEmail = getUserEmail(currentUser?.profile);
  const groupAccess = getGroupAccess(currentUser?.profile, userEmail);
  const isUserAuthenticated = Boolean(auth.isAuthenticated || (currentUser && !currentUser.expired));

  useEffect(() => {
    window.__VP_AUTH_TOKEN = token;
    installApiAuthInterceptor({
      apiBase: API_BASE,
      getToken: () => window.__VP_AUTH_TOKEN || "",
    });
    setTokenReady(Boolean(token));
    return () => {
      window.__VP_AUTH_TOKEN = "";
    };
  }, [token]);

  // Clean stale callback parameters from URL if a sign-in error occurred,
  // and check if a valid user session already exists in storage to recover automatically
  useEffect(() => {
    if (auth.error) {
      if (window.location.search) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      setCheckingRecovery(true);
      auth.userManager?.getUser().then((stored) => {
        if (stored && !stored.expired) {
          setRecoveredUser(stored);
        }
      }).catch(() => {}).finally(() => {
        setCheckingRecovery(false);
      });
    }
  }, [auth.error, auth.userManager]);

  const handleSignIn = () => {
    if (signingIn) return;
    setSigningIn(true);
    if (window.location.search) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    auth.clearStaleState?.().finally(() => {
      auth.signinRedirect().catch(() => setSigningIn(false));
    });
  };

  const handleTryAgain = () => {
    if (window.location.search) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    auth.clearStaleState?.().finally(() => {
      auth.signinRedirect();
    });
  };

  if (auth.isLoading || checkingRecovery) {
    return <AuthMessage title="HRT Security & Infrastructure Resource Center">Loading Ping ID sign-in...</AuthMessage>;
  }

  if (auth.error && !isUserAuthenticated) {
    const isStateError = auth.error.message?.toLowerCase().includes("matching state");
    return (
      <AuthMessage
        title={isStateError ? "Session Expired" : "Sign-in error"}
        action={<button className="auth-button" onClick={handleTryAgain}>Sign in with Ping ID</button>}
      >
        {isStateError
          ? "Your previous sign-in session expired or was already completed. Please sign in again."
          : auth.error.message}
      </AuthMessage>
    );
  }

  if (!isUserAuthenticated) {
    return (
      <AuthMessage
        title="HRT Security & Infrastructure Resource Center"
        action={<button className="auth-button" onClick={handleSignIn} disabled={signingIn}>{signingIn ? "Redirecting..." : "Sign in with Ping ID"}</button>}
      >
        Sign in with your company account to continue.
      </AuthMessage>
    );
  }

  const handleLocalSignOut = () => {
    const fallbackUrl = `${window.location.origin}/`;
    const targetUrl = LOCAL_SIGNOUT_REDIRECT_URI || fallbackUrl;
    analytics.flush();
    analytics.setUser(null);
    window.__VP_AUTH_TOKEN = "";

    Promise.resolve(auth.removeUser()).finally(() => {
      window.location.assign(targetUrl);
    });
  };

  if (groupAccessEnabled
    ? !groupAccess.superadmin && !groupAccess.visitor
    : !ALLOWED_EMAILS.length || !ALLOWED_EMAILS.includes(userEmail)) {
    return (
      <AuthMessage
        title="Access not authorized"
        action={<button className="auth-button" onClick={handleLocalSignOut}>Sign out</button>}
      >
        {groupAccessEnabled
          ? "This account does not have Resource Center access."
          : "This account is not on the temporary production allowlist."}
      </AuthMessage>
    );
  }

  if (!tokenReady) {
    return <AuthMessage title="HRT Security & Infrastructure Resource Center">Preparing your session...</AuthMessage>;
  }

  const profileName = currentUser?.profile?.name || currentUser?.profile?.preferred_username || userEmail;
  return <App profileName={profileName} profileEmail={userEmail} profile={currentUser?.profile} groupAccess={groupAccess} onSignOut={handleLocalSignOut} />;
}